import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Shield, Edit, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const DEFAULT_PERMISSIONS = [
  // Dashboard
  { name: 'view_dashboard', description: 'Ver dashboard', category: 'Dashboard' },
  { name: 'view_metrics', description: 'Ver métricas y estadísticas', category: 'Dashboard' },
  
  // POS
  { name: 'use_pos', description: 'Usar punto de venta', category: 'POS' },
  { name: 'process_sales', description: 'Procesar ventas', category: 'POS' },
  { name: 'apply_discounts', description: 'Aplicar descuentos', category: 'POS' },
  
  // Productos
  { name: 'view_products', description: 'Ver productos', category: 'Productos' },
  { name: 'create_products', description: 'Crear productos', category: 'Productos' },
  { name: 'edit_products', description: 'Editar productos', category: 'Productos' },
  { name: 'delete_products', description: 'Eliminar productos', category: 'Productos' },
  
  // Inventario
  { name: 'view_inventory', description: 'Ver inventario', category: 'Inventario' },
  { name: 'adjust_inventory', description: 'Ajustar inventario', category: 'Inventario' },
  
  // Clientes
  { name: 'view_customers', description: 'Ver clientes', category: 'Clientes' },
  { name: 'create_customers', description: 'Crear clientes', category: 'Clientes' },
  { name: 'edit_customers', description: 'Editar clientes', category: 'Clientes' },
  
  // Cuentas por Cobrar
  { name: 'view_accounts_receivable', description: 'Ver cuentas por cobrar', category: 'Finanzas' },
  { name: 'register_payments', description: 'Registrar pagos', category: 'Finanzas' },
  
  // Proveedores
  { name: 'view_suppliers', description: 'Ver proveedores', category: 'Proveedores' },
  { name: 'create_suppliers', description: 'Crear proveedores', category: 'Proveedores' },
  
  // Compras
  { name: 'view_purchases', description: 'Ver compras', category: 'Compras' },
  { name: 'create_purchases', description: 'Crear órdenes de compra', category: 'Compras' },
  { name: 'receive_purchases', description: 'Recibir compras', category: 'Compras' },
  
  // Ventas
  { name: 'view_sales', description: 'Ver historial de ventas', category: 'Ventas' },
  { name: 'view_sales_reports', description: 'Ver reportes de ventas', category: 'Ventas' },
  
  // IA
  { name: 'view_ai_predictions', description: 'Ver predicciones IA', category: 'IA' },
  
  // Reportes
  { name: 'view_reports', description: 'Ver reportes', category: 'Reportes' },
  { name: 'export_reports', description: 'Exportar reportes', category: 'Reportes' },
  
  // Empresas
  { name: 'view_companies', description: 'Ver empresas', category: 'Empresas' },
  { name: 'manage_companies', description: 'Gestionar empresas', category: 'Empresas' },
  
  // Configuración
  { name: 'view_settings', description: 'Ver configuración', category: 'Configuración' },
  { name: 'manage_settings', description: 'Modificar configuración', category: 'Configuración' },
  { name: 'manage_users', description: 'Gestionar usuarios', category: 'Configuración' },
  { name: 'manage_roles', description: 'Gestionar roles y permisos', category: 'Configuración' },
];

export default function RolesPermissionsPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    initializePermissions();
  }, []);

  const initializePermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions`);
      if (response.data.length === 0) {
        for (const perm of DEFAULT_PERMISSIONS) {
          await axios.post(`${API}/permissions`, perm);
        }
        fetchPermissions();
      }
    } catch (error) {
      console.error('Error initializing permissions:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      toast.error('Error al cargar roles');
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions`);
      setPermissions(response.data);
    } catch (error) {
      toast.error('Error al cargar permisos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, formData);
        toast.success('Rol actualizado');
      } else {
        await axios.post(`${API}/roles`, formData);
        toast.success('Rol creado');
      }
      setOpen(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error('Error al guardar rol');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este rol?')) return;

    try {
      await axios.delete(`${API}/roles/${id}`);
      toast.success('Rol eliminado');
      fetchRoles();
    } catch (error) {
      toast.error('Error al eliminar rol');
    }
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', permissions: [] });
    setEditingRole(null);
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-purple-600 rounded-2xl">
          <Shield className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Roles y Permisos</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los perfiles y sus permisos de acceso
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">
            {roles.length} roles configurados • {permissions.length} permisos disponibles
          </p>
        </div>
        <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full shadow-lg" data-testid="add-role-btn">
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Rol
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del Rol *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Ej: Supervisor de Ventas"
                    data-testid="role-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción *</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    placeholder="Breve descripción del rol"
                    data-testid="role-description-input"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-lg font-semibold">Permisos</Label>
                <p className="text-sm text-muted-foreground">
                  Selecciona los permisos que tendrá este rol
                </p>
                
                {Object.entries(groupedPermissions).map(([category, perms]) => (
                  <div key={category} className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">{category}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {perms.map(perm => (
                        <label
                          key={perm.id}
                          className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            className="h-4 w-4"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{perm.description}</p>
                            <p className="text-xs text-muted-foreground">{perm.name}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {formData.permissions.length} permisos seleccionados
                </p>
                <div className="flex space-x-2">
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-role-btn">
                    {editingRole ? 'Actualizar' : 'Crear'} Rol
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay roles configurados</p>
            </CardContent>
          </Card>
        ) : (
          roles.map(role => (
            <Card key={role.id} className="hover:shadow-lg transition-shadow" data-testid={`role-${role.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-purple-600" />
                    <CardTitle className="text-lg">{role.name}</CardTitle>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(role)}
                      data-testid={`edit-role-${role.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(role.id)}
                      data-testid={`delete-role-${role.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{role.description}</p>
                <div className="pt-2 border-t">
                  <p className="text-xs font-semibold mb-2">
                    {role.permissions.length} permisos asignados
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.slice(0, 5).map(permId => {
                      const perm = permissions.find(p => p.id === permId);
                      return perm ? (
                        <span
                          key={permId}
                          className="inline-flex items-center px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full"
                        >
                          <Check className="h-3 w-3 mr-1" />
                          {perm.category}
                        </span>
                      ) : null;
                    })}
                    {role.permissions.length > 5 && (
                      <span className="text-xs text-muted-foreground">
                        +{role.permissions.length - 5} más
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}